import React, { useLayoutEffect, useRef } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, ListChecks } from 'lucide-react';
import { Button, Input, Textarea } from '../../ui-v2';

// Mismo tope que el servidor (campanasValidator.MAX_DESCRIPCION_SUBTAREA).
const MAX_DESCRIPCION = 2000;

// Columnas de cada fila (y de la cabecera): ordenar · nombre · descripción · borrar. En móvil, nombre y
// descripción comparten la columna central. Clases LITERALES: Tailwind solo genera las que lee enteras.
const COLUMNAS = 'grid-cols-[4.5rem_minmax(0,1fr)_2.25rem] md:grid-cols-[4.5rem_minmax(12rem,20rem)_minmax(0,1fr)_2.25rem]';

/**
 * La descripción empieza con la altura del campo del nombre (h-9, una línea) y crece al escribir.
 * Se mide al cambiar el texto: altura a 'auto' y luego la de su contenido (scrollHeight + bordes).
 */
function DescripcionCreciente({ value, onChange, ...resto }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const bordes = el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.max(el.scrollHeight + bordes, 36)}px`;
  }, [value]);
  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={onChange}
      placeholder="Descripción opcional"
      rows={1}
      maxLength={MAX_DESCRIPCION}
      className="min-h-9 resize-none overflow-hidden py-[7px] leading-5"
      {...resto}
    />
  );
}

export default function Paso2Subtareas({ payload, errores = [], dispatch }) {
  const subs = payload.subtareas || [];
  // Los 422 del servidor sobre las subtareas (campo 'subtareas'): aquí, junto a la lista.
  const erroresSubtareas = errores.filter((e) => e.campo === 'subtareas');

  return (
    // Rediseño (Fase 2): cada subtarea en una fila a todo el ancho; nombre (estrecho) y descripción
    // (ancha) en la misma línea. En pantallas estrechas, uno debajo de otro.
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Lista de tareas que el técnico irá marcando durante cada intervención.
        Es opcional: si no añades ninguna, la cita se marca como realizada al completarse.
      </p>

      {erroresSubtareas.length > 0 && (
        <ul className="border border-red-200 bg-red-50 rounded-lg p-3 text-sm text-red-700 space-y-1">
          {erroresSubtareas.map((e, i) => <li key={i}>{e.mensaje}</li>)}
        </ul>
      )}

      {subs.length === 0 && (
        <div className="border border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-400">
          <ListChecks className="w-6 h-6 mx-auto mb-2 opacity-50" />
          Sin subtareas. Las intervenciones se considerarán completas sin checklist.
        </div>
      )}

      {subs.length > 0 && (
        <div className={`hidden md:grid ${COLUMNAS} gap-3 px-3 text-[11px] uppercase tracking-wide text-slate-400`}>
          <span />
          <span>Nombre</span>
          <span>Descripción (opcional)</span>
          <span />
        </div>
      )}
      <ul className="space-y-2">
        {subs.map((s, i) => (
          <li
            key={i}
            data-subtarea={i}
            className={`border border-slate-200 rounded-lg bg-white p-3 grid ${COLUMNAS} gap-3 items-start`}
          >
            {/* Ordenar: en una caja de la altura del campo (h-9), centrada con su primera línea. */}
            <div className="flex h-9 items-center gap-0.5" data-ordenar>
              <button
                type="button"
                disabled={i === 0}
                onClick={() => dispatch({ type: 'SUBTAREA_MOVE', from: i, to: i - 1 })}
                className="w-6 h-6 grid place-items-center rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Subir"
              >
                <ChevronUp className="w-3.5 h-3.5 text-slate-500" />
              </button>
              <span className="w-4 text-[10px] text-slate-400 text-center font-mono">
                {i + 1}
              </span>
              <button
                type="button"
                disabled={i === subs.length - 1}
                onClick={() => dispatch({ type: 'SUBTAREA_MOVE', from: i, to: i + 1 })}
                className="w-6 h-6 grid place-items-center rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Bajar"
              >
                <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
              </button>
            </div>

            {/* En móvil, nombre y descripción apilados en la columna central; desde md, dos columnas.
                OJO: con `md:contents` la caja desaparece pero `space-y-2` seguía bajando 8 px el
                segundo hijo (la descripción): de ahí el desalineado. Desde md, sin ese margen. */}
            <div className="min-w-0 space-y-2 md:space-y-0 md:contents">
              <Input
                value={s.nombre}
                onChange={(e) =>
                  dispatch({ type: 'SUBTAREA_UPDATE', index: i, field: 'nombre', value: e.target.value })
                }
                placeholder="Nombre de la subtarea"
                aria-label={`Nombre de la subtarea ${i + 1}`}
                className="text-sm"
              />
              <DescripcionCreciente
                value={s.descripcion || ''}
                onChange={(e) =>
                  dispatch({ type: 'SUBTAREA_UPDATE', index: i, field: 'descripcion', value: e.target.value })
                }
                aria-label={`Descripción de la subtarea ${i + 1}`}
              />
            </div>

            <button
              type="button"
              onClick={() => dispatch({ type: 'SUBTAREA_REMOVE', index: i })}
              className="w-9 h-9 grid place-items-center rounded-lg hover:bg-red-50 shrink-0"
              data-borrar
              aria-label="Eliminar subtarea"
              title="Eliminar subtarea"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </li>
        ))}
      </ul>

      <Button
        variant="outline"
        onClick={() => dispatch({ type: 'SUBTAREA_ADD' })}
        className="text-sm"
      >
        <Plus className="w-4 h-4" />
        Añadir subtarea
      </Button>
    </div>
  );
}
